import { html } from 'common-tags';

import { fields } from './shared';

/**
 * @typedef {import('../types').Brand} Brand
 * @typedef {import('../types').Template} Template
 */

/**
 * Minimal bizcard template.
 * @param {Brand} brand - The brand object with theme colors and assets.
 * @returns {Template} - An object containing the email's HTML body and style block.
 */
export default (brand) => ({
  style: html`
    <style>
      :root {
        --background-desktop_only-background-color: #e0e0e0;
        --background-desktop_only-background-image: ;

        --card-background-color: ${brand.colorBackground};
        --card-color: ${brand.colorText};
        --card-font-family: '${brand.fontBase}';
        --card-font-weight: ${brand.fontWeightBase};
        --card-padding: 1.35rem;
        --card-desktop_only-border-radius: 0.75rem;
        --card-desktop_only-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.25);
        --card-header-start-color: ${brand.colorPrimary};
        --card-header-end-color: ${brand.colorSecondary};
        --card-header-clip-path: path('M0 20 s85.8575 -30.8855 168 0 C250.1425 50 296.5605 60 336 60 H0Z');

        --logo-background-image: url('${brand.logoUrl}');
        --logo-height: 2.5rem;
        --logo-text-align: right;

        --profile_image-border-width: 3px;
        --profile_image-border-color: ${brand.colorBackground};
        --profile_image-border-radius: 100%;
        --profile_image-width: 10rem;
        --profile_image-background-color: ${brand.colorSecondary};
        --profile_image-background-image: ;

        --titles-color: ${brand.colorPrimary};
        --titles-font-family: '${brand.fontHeadings}';
        --titles-font-size: 2rem;
        --titles-font-weight: ${brand.fontWeightHeadings};
        --titles-text-align: left;
        --titles-text-transform: none;

        --fields-icons-display: none;
        --fields-icons-color: ${brand.colorText};
        --fields-icons-background-color: transparent;
        --fields-icons-padding: 0px;
        --fields-labels-display: none;
        --fields-labels-color: ${brand.colorText};
        --fields-font-size: 16px;
        --fields-line-height: 1.6;

        --button-background-color: transparent;
        --button-color: ${brand.colorPrimary};
        --button-border-color: ${brand.colorPrimary};
        --button-border-width: 2px;
        --button-border-radius: 0.5rem;
        --button-font-weight: bold;
        --button-text-transform: none;
      }

      body {
        background-color: var(--background-desktop_only-background-color);
        background-image: var(--background-desktop_only-background-image);
        background-size: cover;
        font-family: var(--card-font-family);
        font-weight: var(--card-font-weight);
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        color: var(--titles-color);
        font-family: var(--titles-font-family);
        font-weight: var(--titles-font-weight);
        text-align: var(--titles-text-align);
        text-transform: var(--titles-text-transform);
      }

      h1 {
        font-size: var(--titles-font-size);
      }

      h3 {
        font-size: calc(var(--titles-font-size) * 0.65);
      }

      #logo {
        background-image: var(--logo-background-image);
        background-position: var(--logo-text-align) center;
        background-repeat: no-repeat;
        background-size: contain;
        height: var(--logo-height);
        width: 100%;
      }

      .card {
        background-color: var(--card-background-color);
        color: var(--card-color);
        margin: 0 auto;
        min-height: 100vh;
        overflow: hidden;
        width: 100%;
      }

      .card a {
        color: ${brand.colorText};
      }

      @media (min-width: 576px) {
        nav {
          margin-bottom: 1rem;
        }

        .card {
          border-radius: var(--card-desktop_only-border-radius);
          box-shadow: var(--card-desktop_only-box-shadow);
          min-height: 0;
          width: 3.5in;
        }
      }

      .card header {
        background: linear-gradient(to bottom, var(--card-header-start-color), var(--card-header-end-color));
        color: var(--card-background-color);
        padding: var(--card-padding);
        padding-bottom: calc(var(--card-padding) * 1.75);
        position: relative;
        text-align: var(--header-logo-text-align);
      }

      .card .content {
        padding: var(--card-padding);
      }

      .card footer {
        padding-top: var(--card-padding);
      }

      .card button {
        background-color: var(--button-background-color);
        border: var(--button-border-width) solid var(--button-border-color);
        border-radius: var(--button-border-radius);
        color: var(--button-color);
        font-size: 1.25rem;
        font-weight: var(--button-font-weight);
        padding: 0.5rem;
        text-transform: var(--button-text-transform);
        width: 100%;
      }

      .card connect-button button,
      .card install-button button {
        background-color: transparent;
        color: var(--button-border-color);
        font-size: 1rem;
        margin-top: 0.5rem;
      }

      .card h3 {
        opacity: 0.5;
      }

      hr {
        border: 1px solid currentcolor;
        opacity: 0.5;
        margin: var(--card-padding) 0;
      }

      header {
        aspect-ratio: 7/8;
        background-color: var(--header-background-color);
        background-size: cover;
        position: relative;
        padding-bottom: 50px;
        z-index: 1;
      }

      header::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 60px;
        background-color: var(--card-background-color);
        z-index: 2;

        clip-path: var(--card-header-clip-path);
      }

      .profile-image {
        aspect-ratio: 1/1;
        background-color: var(--profile_image-background-color);
        border: var(--profile_image-border-width) solid var(--profile_image-border-color);
        border-radius: var(--profile_image-border-radius);
        overflow: hidden;
        position: absolute;
        bottom: 0;
        left: var(--card-padding);
        z-index: 1000;
        width: var(--profile_image-width);
      }

      li {
        align-items: center;
        display: grid;
        font-size: var(--fields-font-size);
        grid-template-columns: auto 1fr;
        grid-template-rows: auto auto;
        line-height: var(--fields-line-height);
      }

      li i {
        background-color: var(--fields-icons-background-color);
        border-radius: 12rem;
        color: var(--fields-icons-color);
        display: var(--fields-icons-display);
        font-size: var(--fields-font-size);
        grid-column: 1 / 2;
        grid-row: 1 / 2;
        margin-right: 0.35rem;
        padding: var(--fields-icons-padding);
        text-align: center;
      }

      li a,
      li span {
        align-self: end;
        grid-column: 2 / 3;
        grid-row: 1 / 2;
      }

      li small {
        align-self: start;
        color: var(--fields-labels-color);
        display: var(--fields-labels-display);
        font-size: 0.8em;
        grid-column: 2 / 3;
        grid-row: 2 / 3;
      }
    </style>
  `,
  body: html`
    <nav data-element="header"></nav>

    <div class="card">
      <header data-element="card-header">
        <a href="${brand.companyUrl}" target="_blank">
          <div id="logo" data-element="logo"></div>
        </a>

        <div class="profile-image" data-element="profile-image">
          <profile-image src="{{ image_url }}"></profile-image>
        </div>
      </header>
      <div class="content">
        <div data-element="titles">
          <h1>{{ name }}</h1>
          <h3>{{ title }}</h3>
        </div>

        <hr />

        ${fields}

        <footer data-element="button">
          <user-only>
            <share-button>
              <button>Share</button>
            </share-button>
            <install-button>
              <button>Add to Home Screen</button>
            </install-button>
          </user-only>

          <shared-only>
            <vcard-button>
              <button>Add Contact</button>
            </vcard-button>
          </shared-only>
        </footer>
      </div>
    </div>
  `
});
